import { Icon } from '../../atoms/Icon/Icon';
import type { FiltersBarFilter } from './FiltersBar';

/**
 * The six chips on the Figma canvas of `Filters Bar` (781:15281), in its order.
 *
 * `Brands` is first and carries `types: ['competitors']` — Figma draws it only
 * on `Type=Competitors` and hides it on `Type=Default`, so a default bar renders
 * the other five. Keeping it in the array rather than in a second fixture is
 * what lets the same `filters` prop serve both variants.
 *
 * Figma shows only each chip's icon, its label and its count badge, so the icons
 * and counts here are the design's and the option lists behind them are invented
 * to produce them. The Brands list is the one the `Filter Chip` panel itself
 * shows (93:1707).
 *
 * The icons are the six symbols added to the Icons page in the 2026-09 update
 * and swapped into each chip's leading slot: `brand` (639:14145), `channel`
 * (639:14142), `intent` (639:14144), `topics` (639:14143), `conversations`
 * (639:14141), `sentiments` (639:14140). Note the singular/plural mismatch with
 * the labels — the symbols are named as Figma names them.
 *
 * **All six counts read 3 as of 2026-08-11.** They previously read 3, 4, 12, 18,
 * 2, 3; swapping every chip's counter replaced the instances and dropped their
 * text overrides. This mirrors the file as it now stands — see plan.md §7 item
 * 67, and `variedCountFilters` below for the multi-digit coverage that reset
 * removed from the canvas.
 */
const opts = (...labels: string[]) =>
  labels.map((label) => ({ value: label.toLowerCase().replace(/[^a-z0-9]+/g, '-'), label }));

const pick = (options: ReturnType<typeof opts>, count: number) =>
  options.slice(0, count).map((o) => o.value);

const BRANDS = opts('Grameenphone', 'Banglalink', 'Robi', 'Airtel', 'Teletalk');

const CHANNELS = opts('Facebook', 'Instagram', 'X', 'YouTube', 'TikTok', 'LinkedIn', 'Reddit');

const INTENTS = opts(
  'Complaint',
  'Praise',
  'Question',
  'Purchase intent',
  'Cancellation',
  'Comparison',
  'Recommendation',
  'Support request',
  'Billing issue',
  'Feature request',
  'Outage report',
  'Refund request',
  'Renewal',
  'Upgrade',
);

const TOPICS = opts(
  'Pricing',
  'Network coverage',
  'Data packs',
  'Customer service',
  'Roaming',
  'Billing',
  'App experience',
  'Store experience',
  'Promotions',
  'Call quality',
  'Internet speed',
  'Recharge',
  'SIM activation',
  'Device offers',
  'Loyalty rewards',
  'Contract terms',
  'Advertising',
  'Sponsorships',
  'Sustainability',
  'Careers',
);

/*
 * Longer than the five the `Filters Bar` canvas needs, because the `Example`
 * frame (450:52850) shows this chip reading 12 — a count the shorter list could
 * not reach, since `pick` clamps to the options that exist.
 */
const CONVERSATIONS = opts(
  'Direct message',
  'Comment',
  'Mention',
  'Review',
  'Support ticket',
  'Reply',
  'Quote post',
  'Repost',
  'Story mention',
  'Forum thread',
  'Blog comment',
  'Live chat',
  'App store review',
  'Video comment',
);

/*
 * Three, not four. `Mixed` was here until 2026-09 and was never in the Figma
 * file — a search of every page finds no such text — so dropping it moves the
 * fixtures towards the design rather than away from it. It also lets sentiment
 * bind to `component/progress`, which defines exactly three tones.
 */
const SENTIMENTS = opts('Positive', 'Neutral', 'Negative');

export const filtersBarFilters: FiltersBarFilter[] = [
  {
    id: 'brands',
    label: 'Brands',
    icon: <Icon name="brand" />,
    options: BRANDS,
    defaultSelected: pick(BRANDS, 3),
    types: ['competitors'],
  },
  {
    id: 'channels',
    label: 'Channels',
    icon: <Icon name="channel" />,
    options: CHANNELS,
    defaultSelected: pick(CHANNELS, 3),
  },
  {
    id: 'intents',
    label: 'Intents',
    icon: <Icon name="intent" />,
    options: INTENTS,
    defaultSelected: pick(INTENTS, 3),
  },
  {
    id: 'topics',
    label: 'Topics',
    icon: <Icon name="topics" />,
    options: TOPICS,
    defaultSelected: pick(TOPICS, 3),
  },
  {
    id: 'conversations',
    label: 'Conversations',
    icon: <Icon name="conversations" />,
    options: CONVERSATIONS,
    defaultSelected: pick(CONVERSATIONS, 3),
  },
  {
    id: 'sentiments',
    label: 'Sentiments',
    icon: <Icon name="sentiments" />,
    options: SENTIMENTS,
    defaultSelected: pick(SENTIMENTS, 3),
  },
];

/**
 * The counts the canvas carried before the counter swap. Kept as a fixture
 * because they are the only thing that exercises a two-digit badge, which is
 * where `FilterChipCounter` stops hugging a single 7px glyph and the chip has
 * to grow around it.
 */
export const variedCountFilters: FiltersBarFilter[] = [
  { ...filtersBarFilters[0], defaultSelected: pick(BRANDS, 3) },
  { ...filtersBarFilters[1], defaultSelected: pick(CHANNELS, 4) },
  { ...filtersBarFilters[2], defaultSelected: pick(INTENTS, 12) },
  { ...filtersBarFilters[3], defaultSelected: pick(TOPICS, 18) },
  { ...filtersBarFilters[4], defaultSelected: pick(CONVERSATIONS, 2) },
  { ...filtersBarFilters[5], defaultSelected: pick(SENTIMENTS, 3) },
];
